'use client';

import React, { useState, useCallback } from 'react';
import { trackLeadFormSubmission } from '@/lib/analytics';
import { containsNonLatinText, NON_ENGLISH_TEXT_ERROR } from '@/lib/text-validation';

// Country list for form
const COUNTRIES = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany',
  'France', 'Netherlands', 'Belgium', 'Ireland', 'New Zealand',
  'Singapore', 'Japan', 'South Korea', 'India', 'Brazil',
  'Mexico', 'Spain', 'Italy', 'Sweden', 'Norway',
  'Denmark', 'Finland', 'Switzerland', 'Austria', 'Poland',
  'South Africa', 'United Arab Emirates', 'Saudi Arabia', 'Israel', 'Other',
];

export interface LeadCaptureFormProps {
  /** Unique identifier for tracking (e.g., 'cybersecurity-maturity-assessment') */
  source: string;
  /** Title displayed at the top of the form */
  title: string;
  /** Description text below the title */
  description: string;
  /** What the user will receive (shown in success message) */
  deliverable?: string;
  /** Optional summary of the assessment/calculation results to include in the message */
  resultsSummary?: string;
  /** Optional callback when form is successfully submitted */
  onSuccess?: () => void;
  /** Optional className for the outer container */
  className?: string;
}

interface FormData {
  name: string;
  email: string;
  company: string;
  country: string;
  phone: string;
  message: string;
}

export function LeadCaptureForm({
  source,
  title,
  description,
  deliverable = 'a personalized consultation',
  resultsSummary,
  onSuccess,
  className = '',
}: LeadCaptureFormProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    country: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleFormChange = useCallback((field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Warn immediately if the user types non-English (non-Latin script) characters
    if (field !== 'email' && containsNonLatinText(value)) {
      setErrorMessage(NON_ENGLISH_TEXT_ERROR);
      setSubmitStatus('error');
    } else {
      setSubmitStatus((prev) => (prev === 'error' ? 'idle' : prev));
    }
  }, []);

  const handleFormSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    // Block non-English (non-Latin script) input in free-text fields
    if (
      containsNonLatinText(formData.name) ||
      containsNonLatinText(formData.company) ||
      containsNonLatinText(formData.message)
    ) {
      setErrorMessage(NON_ENGLISH_TEXT_ERROR);
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Build the full message including results summary and any user message
    let fullMessage = '';
    if (resultsSummary) {
      fullMessage = resultsSummary + '\n\n';
    }
    fullMessage += `Country: ${formData.country}`;
    if (formData.phone) {
      fullMessage += `\nPhone: ${formData.phone}`;
    }
    if (formData.message) {
      fullMessage += `\n\nAdditional Notes:\n${formData.message}`;
    }

    // Split name into first and last name for API
    const nameParts = formData.name.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName,
          email: formData.email,
          company: formData.company,
          message: fullMessage,
          formName: 'Lead Capture - Expert Review',
          src: source,
          pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', country: '', phone: '', message: '' });
        trackLeadFormSubmission('Lead Capture - Expert Review', source, undefined, formData.email);
        onSuccess?.();
      } else {
        const data = await response.json().catch(() => ({}));
        setErrorMessage(data.error || 'Something went wrong. Please try again.');
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, resultsSummary, source, onSuccess]);

  return (
    <div className={`bg-gradient-to-r from-[#2cc3d1] to-[#2813e8] rounded-lg p-6 sm:p-8 ${className}`}>
      <div className="max-w-2xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
          {title}
        </h2>
        <p className="text-white/80 mb-6">
          {description}
        </p>

        {submitStatus === 'success' ? (
          <div className="bg-white rounded-lg p-6 text-center">
            <div className="text-green-600 text-5xl mb-4">✓</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Request Submitted!</h3>
            <p className="text-gray-600 mb-3">
              Thank you for your interest. Our team will review your results and contact you within 1 business day with {deliverable}.
            </p>
            <p className="text-gray-500 text-sm">
              Have additional details to share? Simply reply to our email when you receive it, or include any relevant documents about your environment.
            </p>
          </div>
        ) : (
          <form onSubmit={handleFormSubmit} name={`${source}-lead-form`} id={`${source}-lead-form`} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor={`${source}-lead-name`} className="block text-sm font-medium text-white mb-1">Full Name *</label>
                <input
                  id={`${source}-lead-name`}
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-white focus:border-transparent"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label htmlFor={`${source}-lead-email`} className="block text-sm font-medium text-white mb-1">Work Email *</label>
                <input
                  id={`${source}-lead-email`}
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-white focus:border-transparent"
                  placeholder="john@company.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor={`${source}-lead-company`} className="block text-sm font-medium text-white mb-1">Company *</label>
                <input
                  id={`${source}-lead-company`}
                  type="text"
                  required
                  value={formData.company}
                  onChange={(e) => handleFormChange('company', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-white focus:border-transparent"
                  placeholder="Company Name"
                />
              </div>
              <div>
                <label htmlFor={`${source}-lead-country`} className="block text-sm font-medium text-white mb-1">Country *</label>
                <select
                  id={`${source}-lead-country`}
                  required
                  value={formData.country}
                  onChange={(e) => handleFormChange('country', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-white focus:border-transparent"
                >
                  <option value="">Select your country</option>
                  {COUNTRIES.map((country) => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor={`${source}-lead-phone`} className="block text-sm font-medium text-white mb-1">Phone (Optional)</label>
              <input
                id={`${source}-lead-phone`}
                type="tel"
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-white focus:border-transparent"
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div>
              <label htmlFor={`${source}-lead-message`} className="block text-sm font-medium text-white mb-1">Additional Notes (Optional)</label>
              <textarea
                id={`${source}-lead-message`}
                value={formData.message}
                onChange={(e) => handleFormChange('message', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-white focus:border-transparent resize-none"
                placeholder="Any specific questions or context about your organization..."
                rows={3}
              />
            </div>

            {submitStatus === 'error' && (
              <div className="bg-red-100 border border-red-300 rounded-lg p-3 text-red-700 text-sm">
                {errorMessage || 'There was an error submitting your request. Please try again or contact us directly.'}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-6 py-3 bg-white text-[#2813e8] font-semibold rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Request Expert Review'}
            </button>

            <p className="text-white/60 text-xs text-center">
              Your assessment results will be included with your request. We typically respond within 1 business day.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

export default LeadCaptureForm;
